/**
 * Post Editor Page
 * Feature 014: Unified Multi-AI Post Generation Flow
 * Displays selected post variant with manual and AI-assisted editing capabilities
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { usePost } from '../hooks/usePost';
import { useImageGeneration } from '../hooks/useImageGeneration';
import { GeneratedPostDisplayMemo } from '../components/posts/GeneratedPostDisplay';
import { AspectRatioDialogMemo } from '../components/images/AspectRatioDialog';
import { GeneratedImageDisplay } from '../components/images/GeneratedImageDisplay';
import { ImagePromptEditor } from '../components/images/ImagePromptEditor';
import { ImageGenerationStatus } from '../components/images/ImageGenerationStatus';
import { Button } from '../components/ui/button';
import { ArrowLeft, ImageIcon, Sparkles } from 'lucide-react';

const PostEditorPage = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();

  // ============================================
  // STATE
  // ============================================
  const [showAspectRatioDialog, setShowAspectRatioDialog] = useState(false);

  // ============================================
  // HOOKS
  // ============================================
  const { post, isLoading, notFound } = usePost(postId as Id<'posts'> | undefined);

  // Image generation hook
  const {
    step,
    promptId,
    promptText,
    error: imageError,
    errorCode: imageErrorCode,
    generate,
    generateWithPrompt,
    isGenerating,
  } = useImageGeneration({
    postId: postId as Id<'posts'>,
  });

  // Fetch latest image for this post
  const latestImage = useQuery(
    api.ai.queries.getLatestImageByPost,
    postId ? { postId: postId as Id<'posts'> } : 'skip'
  );

  // Fetch prompt for the latest image
  const latestPrompt = useQuery(
    api.ai.queries.getLatestPromptByPost,
    postId ? { postId: postId as Id<'posts'> } : 'skip'
  );

  // ============================================
  // COMPUTED VALUES
  // ============================================
  const hasImage = !!latestImage && latestImage.status === 'completed';
  const isImageGenerating = step === 'generating_prompt' || step === 'generating_image';

  // Use latestPrompt from query (more reliable than hook state on page load)
  const actualPromptId = latestPrompt?._id || promptId;
  const actualPromptText = latestPrompt?.promptText || promptText;
  const canEditPrompt = !!actualPromptId && !!actualPromptText && actualPromptText.length >= 10;

  // ============================================
  // EVENT HANDLERS
  // ============================================
  const handleBack = () => {
    navigate(-1);
  };

  const handlePostUpdated = () => {
    // Callback when post is saved - could show toast notification in future
  };

  const handleGenerateImage = useCallback(() => {
    setShowAspectRatioDialog(true);
  }, []);

  const handleAspectRatioSelect = useCallback(
    async (selectedRatio: '1:1' | '4:5' | '9:16' | '16:9') => {
      setShowAspectRatioDialog(false);
      await generate(selectedRatio);
    },
    [generate]
  );

  const handleRegenerateWithPrompt = useCallback(
    async (prompt: string) => {
      await generateWithPrompt(prompt);
    },
    [generateWithPrompt]
  );

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-4" />
          <div className="h-64 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h3 className="font-semibold text-red-900">Post Not Found</h3>
          <p className="mt-2 text-sm text-red-700">
            The requested post could not be found.
          </p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={handleBack}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Edit Post</h1>
        <p className="mt-2 text-gray-600">
          Refine your post with manual editing or AI assistance
        </p>
      </div>

      {/* Post Editor - Reuses existing component */}
      <GeneratedPostDisplayMemo
        post={post}
        onPostUpdated={handlePostUpdated}
      />

      {/* Image Generation Section */}
      <div className="mt-8 border-t pt-8">
        <h2 className="mb-4 text-2xl font-bold text-gray-900">Post Image</h2>

        {/* No Image State */}
        {!hasImage && !isImageGenerating && step !== 'error' && (
          <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
            <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No image generated yet
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Generate an eye-catching image for your post
            </p>
            <Button onClick={handleGenerateImage}>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate Image
            </Button>
          </div>
        )}

        {/* Generating State */}
        {isImageGenerating && (
          <ImageGenerationStatus
            step={step}
            promptText={promptText || undefined}
            error={imageError || undefined}
          />
        )}

        {/* Error State */}
        {step === 'error' && imageError && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-6">
            <h3 className="mb-2 text-lg font-semibold text-destructive">
              Image Generation Failed
            </h3>
            <p className="mb-2 text-sm">{imageError}</p>
            {imageErrorCode && (
              <p className="mb-4 text-xs text-muted-foreground">
                Error Code: {imageErrorCode}
              </p>
            )}
            <Button onClick={handleGenerateImage} variant="outline">
              Try Again
            </Button>
          </div>
        )}

        {/* Image Display + Editor */}
        {hasImage && latestImage && (
          <div className="space-y-6">
            {/* Prompt Editor - Only show if we have valid prompt data */}
            {canEditPrompt && (
              <ImagePromptEditor
                promptId={String(actualPromptId)}
                postId={postId || ''}
                promptText={actualPromptText}
                onRegenerateWithPrompt={handleRegenerateWithPrompt}
                isGenerating={isGenerating}
              />
            )}

            {/* Image Display */}
            <GeneratedImageDisplay
              imageUrl={latestImage.imageUrl || ''}
              aspectRatio={latestImage.aspectRatio}
              width={latestImage.width}
              height={latestImage.height}
              fileSize={latestImage.fileSize}
              isLoading={isImageGenerating}
            />
          </div>
        )}
      </div>

      {/* Aspect Ratio Dialog */}
      <AspectRatioDialogMemo
        open={showAspectRatioDialog}
        onClose={() => setShowAspectRatioDialog(false)}
        onSelect={handleAspectRatioSelect}
        isGenerating={isImageGenerating}
        defaultRatio="9:16"
      />
    </div>
  );
};

export { PostEditorPage };
